import SessionStore from "../../../services/session/SessionStore";
import BrandConfigStore from "../../../services/config/BrandConfigStore";

const isDev =
  window.location.href.includes("localhost") ||
  window.location.href.includes("https://qa.") ||
  window.location.href.includes("https://next.") ||
  window.location.href.includes("https://milestone.");

/**
 * require all of the permissions to be on the user
 * @param {string} permissions - permission strings, e.g. requirePermissions("ShowReviews", "ReviewTagManager")
 * @return {boolean} - true if the route is accessible
 */
export function requirePermissions(...permissions) {
  return !!SessionStore.user && permissions.every(p => SessionStore.user.permissions.includes(p));
}

/**
 * like requirePermissions, but only requires at least one permission, not all
 * @param {string} permissions
 * @return {boolean} - true if the route is accessible
 */
export function requireOnePermission(...permissions) {
  return !!SessionStore.user && permissions.some(p => SessionStore.user.permissions.includes(p));
}

/**
 * requirement where all of the specified account service keys must be enabled
 * @param {string} accountServiceNames - for example "listingManagement". in typescript syntax, `keyof AccountServices`
 *                                  See @type {AccountServices}
 * @return {boolean} - true if the route is accessible
 */
export function accountServices(...accountServiceNames) {
  return !!SessionStore.account && accountServiceNames.every(s => !!SessionStore.account.services[s]);
}

/**
 *
 * @param {string} restrictions - names of brand config restrictions that hide the tab `keyof BrandConfigRestrictions`,
 *                                for example "hideReviews". If any is hidden, the tab is disabled
 * @return {boolean} - true if the route is accessible
 */
export function hideIfAnyRestriction(...restrictions) {
  return !!BrandConfigStore.brandConfig && // enables content only if BrandConfig is loaded, and every brandConfigRestriction is disabled
    // brand config restriction feature flags are backwards, in the form "hideMyFeatureName"
    // explicitly checks for false
    // this is to prevent accidentally exposing features from a new client deploy if the server is behind
    // (and therefore the restriction would be `undefined` which is falsy)
    restrictions.every(r => BrandConfigStore.brandConfig.restrictions[r] === false);
}

/**
 *
 * @param {string} restrictions - names of brand config restrictions that hide the tab `keyof BrandConfigRestrictions`,
 *                                for example "hideReviews". This is useful for scenarios where there are duplicate keys.
 * @return {boolean} - true if the route is accessible
 */
export function hideIfAllRestrictions(...restrictions) {
  return !!BrandConfigStore.brandConfig && restrictions.every(r => BrandConfigStore.brandConfig.restrictions[r])
}

/**
 * display this route in development hosts only
 * @return {boolean} - true if the route is accessible
 */
export function devOnly() {
  return isDev;
}

/************************************
 * General feature access checks below
 */

/** generalFeatureRouteChecks */
export function snapshotAllowed() {
  return requirePermissions("ShowSnapshot") && hideIfAnyRestriction("hideSnapshot");
}

export function reviewsAllowed() {
  return requirePermissions("ShowReviews") && hideIfAnyRestriction("hideReviews");
}

export function reviewsStoreComparisonAllowed() {
  return reviewsAllowed() && hideIfAnyRestriction("hideReviewStoreComparison");
}

export function sentimentAllowed() {
  return requirePermissions("ShowSentiment") && hideIfAnyRestriction("hideSentiment");
}

export function sentimentStoreComparisonAllowed() {
  return sentimentAllowed() && hideIfAnyRestriction("hidePulseStoreComparison");
}

export function mediaAllowed() {
  return requirePermissions("ShowMedia") && hideIfAnyRestriction("hideMedia");
}

/** returns whether the base social feature is allowed and enabled */
export function socialAllowed() {
  return requirePermissions("ShowChatter") && hideIfAnyRestriction("hideChatter2");
}

export function socialReportsAllowed() {
  return socialAllowed() && hideIfAnyRestriction("hideSocialStoreComparison");
}

/** returns whether tracking of social post engagement and metrics is enabled.
 * Allowed for all users if enabled */
export function socialEngagementAllowed() {
  return socialAllowed() && accountServices("socialEngagement");
}

/**
 * returns whether social commenting, the ability to respond to social posts from the activity stream,
 * is allowed and enabled.
 * "Allowed," in this context, means if the user has at least drafting capability.
 */
export function socialCommentingAllowed() {
  return socialAllowed()
    && accountServices("socialCommenting")
    && requireOnePermission("SocialCommenter", "SubmitSocialCommenter");
}

/**
 * returns whether social publishing, the ability to schedule and publish posts to business pages.
 * is allowed and enabled.
 * "Allowed," in this context, means if the user has at least drafting capability.
 */
export function socialPublishingAllowed() {
  return socialAllowed()
    && accountServices("socialPublishing")
    && requireOnePermission("SocialPoster", "SubmitChatterPost")
}

/**
 * returns whether the user is allowed to do any social administration
 */
export function socialSettingsAllowed() {
  return socialAllowed() && requireOnePermission("ManageExternalAccounts", "AddExternalAccounts");
}

/**
 * returns true if google listing management is enabled for the active user's account
 */
export function googleListingManagementAllowed() {
  return accountServices("googleListingManagement")
}

export function listingsAllowed() {
  return requirePermissions("ShowListings") && hideIfAnyRestriction("hideListings");
}

export function listingOptimizationAllowed() {
  return listingsAllowed() && hideIfAnyRestriction("hideListingOptimizationReport");
}

export function listingMissingReportAllowed() {
  return listingsAllowed() && hideIfAnyRestriction("hideListingMissingReport");
}

export function listingDuplicatesReportAllowed() {
  return listingsAllowed() && hideIfAnyRestriction("hideListingDuplicatesReport");
}

export function listingSearchAnalyticsReportAllowed() {
  return listingsAllowed() && requirePermissions("ShowGoogleInsights") && hideIfAnyRestriction("hideSearchAnalyticsStoreComparison");
}

export function listingAccuracyReportAllowed() {
  return listingsAllowed() && hideIfAnyRestriction("hideListingManagementAccuracyReport");
}

export function reviewBuilderAllowed() {
  return requirePermissions("ReviewBuilderAccess") && hideIfAnyRestriction("hideReviewGeneration");
}

export function rankingsAllowed() {
  return requirePermissions("ShowRankings") && hideIfAnyRestriction("hideRankings");
}

export function workflowAllowed() {
  return requirePermissions("ShowWorkflow") && hideIfAnyRestriction("hideWorkflow2");
}
export function workflowNotificationSettingsAllowed() {
  return requirePermissions("ShowWorkflow") && hideIfAnyRestriction("hideWorkflow2") && !notificationSettingsAllowed();
}

export function notificationSettingsAllowed() {
  return requirePermissions("NotificationSettingsAdmin") && hideIfAnyRestriction("hideNotificationsSettings");
}

export function scoringAllowed() {
  return requirePermissions("ShowScoreReport") && hideIfAnyRestriction("hideScores", "hideScores2")
}

export function scoringStoreComparisonAllowed() {
  return scoringAllowed() && hideIfAnyRestriction("hideScoresStoreComparison");
}

export function workflowAdminAllowed() {
  return workflowAllowed() && requirePermissions("TaskAdmin");
}

export function emailAlertsAllowed() {
  return requirePermissions("ShowEmailAlerts") && hideIfAnyRestriction("hideEmailAlertsAdmin")
}

export function emailAlertsAdminAllowed() {
  return requirePermissions("EmailAlertManagement");
}

export function analyticsStudioAllowed() {
  return requirePermissions("ShowInsights") && hideIfAnyRestriction("hideInsights");
}

export function isGlobalAdmin() {
  return requirePermissions("GlobalAccess");
}

export function userManagementAllowed() {
  return requirePermissions("UserManagement");
}

export function groupManagementAllowed() {
  return requirePermissions("GroupManagement");
}

export function scoreManagementAllowed() {
  return requirePermissions("ReadScoreConfig", "EditScoreConfig") && hideIfAnyRestriction("hideScores", "hideScores2");
}

export function locationManagementAllowed() {
  return requireOnePermission('AddLocation', 'EditLocation', 'DeleteLocation', 'RestoreLocation');
}

export function accountManagementAllowed() {
  return requirePermissions("AccountManagement");
}

export function reportManagementAllowed() {
  return requirePermissions("ReportManagement");
}

export function brandManagementAllowed() {
  return requirePermissions("BrandManagement");
}

export function roleManagementAllowed() {
  return requirePermissions("AccountOwner", "RoleCreation");
}

export function credentialManagementAllowed() {
  return requireOnePermission("ManageExternalAccounts", "AddExternalAccounts");
}

export function instantAuditsAllowed() {
  return requirePermissions("InstantAuditsAdmin");
}

export function uploadToolAllowed() {
  // show if the preference is enabled for the account, or if the user was impersonated by an internal admin.
  // This is kinda hacky. Transitioned from a kinda hacky alternative where there was a secret url param added to the locations editor
  // that unlocked the upload tool.
  const isAdminImpersonatingUser = !!SessionStore.user && SessionStore.user.impersonatedByUser && SessionStore.user.impersonatedByUser.internalAdmin;
  return hideIfAnyRestriction("hideUploadTool") || isAdminImpersonatingUser || false;
}

export function isImpersonatedByGlobalAccessAdmin() {
  const isAdminImpersonatingUser = !!SessionStore.user && SessionStore.user.impersonatedByUser && SessionStore.user.impersonatedByUser.internalAdmin;

  return isAdminImpersonatingUser
}

// TODO - Use this for local pages reports and location editor
export function localPagesAllowed() {
  // show if the preference is enabled for the account, or if the user was impersonated by an internal admin.
  return accountServices("localPages");
}

export function hasRestaurantMenus() {
  return hideIfAnyRestriction("hideRestaurantMenus");
}


export function hasSmartResponse() {
  return requirePermissions("ReviewResponseGeneration") && hideIfAnyRestriction("hideReviewResponseGeneration");
}

export function hasLocationScheduling() {
  return hideIfAnyRestriction("hideLocationScheduling")
}